import { useEffect, useState } from 'react';

import { PRESENTER_ANCHOR_ATTR, PRESENTER_HANDOFF_MS, PRESENTER_TRACK_START, stepPresenterAnchor } from './websiteAgentLayout';
import type { PresenterAnchorTrack, PresenterBox } from './websiteAgentLayout';

export interface PresenterAnchorState {
  /** Where the resting presenter stands, or null for its usual corner. */
  box: PresenterBox | null;
  /**
   * True for PRESENTER_HANDOFF_MS after the presenter changes place: corner,
   * an anchor, holding between anchors, another anchor. Only that move animates:
   * an anchor that moves while the presenter already stands in it (the host
   * section scrolling into its pinned spot) is tracked instantly, or the tile
   * would trail the page by a transition.
   */
  handoff: boolean;
  /** performance.now() of the last change of place. */
  switchedAt: number;
  /** The viewport the box was measured in (the corner box is derived from it). */
  viewport: { height: number; width: number };
}

const sameBox = (a: PresenterBox | null, b: PresenterBox | null) =>
  a === b || (!!a && !!b && a.left === b.left && a.top === b.top && a.width === b.width && a.height === b.height);

const INITIAL: PresenterAnchorState = {
  box: null,
  handoff: false,
  switchedAt: Number.NEGATIVE_INFINITY,
  viewport: { height: 0, width: 0 },
};

const rectOf = (el: Element): PresenterBox => {
  const r = el.getBoundingClientRect();
  return { height: r.height, left: r.left, top: r.top, width: r.width };
};

const sameElements = (a: Element[], b: Element[]) => a.length === b.length && a.every((el, i) => el === b[i]);

/** Scroll quiet for this long counts as settled (stepPresenterAnchor). */
const SCROLL_SETTLE_MS = 160;

/** Which place the presenter is in: the corner, holding, or one anchor. */
const placeOf = (track: PresenterAnchorTrack) => (track.holding ? -2 : track.index);

/**
 * Tracks the host page's `data-floe-presenter-anchor` elements and reports the box
 * the resting presenter should occupy (stepPresenterAnchor: an anchor wholly on
 * screen, holding still between two anchors, or null for the corner).
 *
 * The anchor can sit inside a `position: sticky` parent (floe.so pins it beside a
 * product frame for a whole scroll story), so its document position is not
 * stable — the box is re-measured in viewport coordinates on scroll, on resize,
 * and when the anchor or its parent changes size (a host re-render can move it
 * with no scroll at all), at most once per frame. Reads rects; writes nothing to
 * the host.
 *
 * `enabled` is the caller's whole gate (dormant agent, desktop tier, live-video
 * tile). Disabled, it returns null (keep the classic corner) and does no work.
 */
export const usePresenterAnchor = (enabled: boolean): PresenterAnchorState | null => {
  const [state, setState] = useState<PresenterAnchorState>(INITIAL);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      return;
    }
    let frame = 0;
    let settle = 0;
    let handoffEnd = 0;
    let observed: Element[] = [];
    let track = PRESENTER_TRACK_START;
    // Assigned once `schedule` exists; `measure` re-points it at a replaced anchor.
    let resize: ResizeObserver | null = null;
    const measure = (settled: boolean) => {
      frame = 0;
      const viewport = { height: window.innerHeight, width: window.innerWidth };
      const anchors = [...document.querySelectorAll(`[${PRESENTER_ANCHOR_ATTR}]`)];
      if (!sameElements(anchors, observed)) {
        // The track holds anchors by index: a changed set starts it afresh.
        observed = anchors;
        track = PRESENTER_TRACK_START;
        resize?.disconnect();
        for (const el of anchors) {
          resize?.observe(el);
          if (el.parentElement) {
            resize?.observe(el.parentElement);
          }
        }
      }
      const moved = placeOf(track);
      track = stepPresenterAnchor(track, anchors.map(rectOf), viewport.height, window.scrollY, settled);
      const { box } = track;
      const now = performance.now();
      const changedPlace = placeOf(track) !== moved;
      setState((prev) => {
        const switchedAt = changedPlace ? now : prev.switchedAt;
        const handoff = now - switchedAt < PRESENTER_HANDOFF_MS;
        const unchanged =
          sameBox(prev.box, box) &&
          prev.handoff === handoff &&
          prev.viewport.width === viewport.width &&
          prev.viewport.height === viewport.height;
        return unchanged ? prev : { box, handoff, switchedAt, viewport };
      });
      if (changedPlace) {
        // End the handoff even if nothing scrolls or resizes again, so the tile's
        // transition never outlives the move it was for.
        window.clearTimeout(handoffEnd);
        handoffEnd = window.setTimeout(() => {
          setState((prev) => (prev.handoff ? { ...prev, handoff: false } : prev));
        }, PRESENTER_HANDOFF_MS);
      }
    };
    const schedule = () => {
      if (!frame) {
        frame = window.requestAnimationFrame(() => measure(false));
      }
    };
    const onScroll = () => {
      schedule();
      window.clearTimeout(settle);
      settle = window.setTimeout(() => measure(true), SCROLL_SETTLE_MS);
    };
    resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
    schedule();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
      window.clearTimeout(settle);
      window.clearTimeout(handoffEnd);
      resize?.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', schedule);
    };
  }, [enabled]);

  return enabled ? state : null;
};
